import { Dispatch } from 'redux'
import { api, apiErrorHandler, getApiRequestConfig, Response } from '../../api'
import { GetState } from '../reducers'
import { Advertisement, AdvertisementStatus } from '../reducers/advertisement.reducer'

export enum AdvertisementActionTypes {
    ADD_ADVERTISEMENT = 'ADD_ADVERTISEMENT',
    UPDATE_ADVERTISEMENT = 'UPDATE_ADVERTISEMENT',
    FETCH_ADVERTISEMENTS = 'FETCH_ADVERTISEMENTS',
    DELETE_ADVERTISEMENT = 'DELETE_ADVERTISEMENT'
}

export interface AddAdvertisementAction {
    type: AdvertisementActionTypes.ADD_ADVERTISEMENT
    data: Advertisement
}

export interface UpdateAdvertisementAction {
    type: AdvertisementActionTypes.UPDATE_ADVERTISEMENT
    data: Advertisement
}

export interface FetchAdvertisementsAction {
    type: AdvertisementActionTypes.FETCH_ADVERTISEMENTS
    data: Advertisement[]
}

export interface DeleteAdvertisementAction {
    type: AdvertisementActionTypes.DELETE_ADVERTISEMENT
    data: string
}

export type AdvertisementActions = AddAdvertisementAction | FetchAdvertisementsAction | UpdateAdvertisementAction | DeleteAdvertisementAction

interface AdvertisementInput {
    image: string
    link: string
    status: AdvertisementStatus
}

export const addAdvertisement = (input: AdvertisementInput) => async (dispatch: Dispatch, getState: GetState) => {
    const authSuperAdmin = getState().authSuperAdmin
    const config = getApiRequestConfig(authSuperAdmin?.['auth-token'])

    return api.post<Response<Advertisement>>('advertisement', input, config).then(response => {
        if (response.status === 200) {
            dispatch<AddAdvertisementAction>({
                type: AdvertisementActionTypes.ADD_ADVERTISEMENT,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Added')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to add'))
    })
}

export const updateAdvertisement = (input: AdvertisementInput, advertisementId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const authSuperAdmin = getState().authSuperAdmin
    const config = getApiRequestConfig(authSuperAdmin?.['auth-token'])

    return api.put<Response<Advertisement>>(`advertisement/${advertisementId}`, input, config).then(response => {
        if (response.status === 200) {
            dispatch<UpdateAdvertisementAction>({
                type: AdvertisementActionTypes.UPDATE_ADVERTISEMENT,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || 'Updated')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to update'))
    })
}

export const fetchAdvertisements = () => async (dispatch: Dispatch, getState: GetState) => {
    const authSuperAdmin = getState().authSuperAdmin
    const config = getApiRequestConfig(authSuperAdmin?.['auth-token'])

    return api.get<Response<Advertisement[]>>('advertisement', config).then(response => {
        if (response.status === 200) {
            dispatch<FetchAdvertisementsAction>({
                type: AdvertisementActionTypes.FETCH_ADVERTISEMENTS,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || '')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch'))
    })
}

export const fetchActiveAdvertisements = () => async (dispatch: Dispatch) => {
    return api.get<Response<Advertisement[]>>('advertisement/active').then(response => {
        if (response.status === 200) {
            dispatch<FetchAdvertisementsAction>({
                type: AdvertisementActionTypes.FETCH_ADVERTISEMENTS,
                data: response.data.data
            })
            return Promise.resolve<string>(response.data.message || '')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to fetch'))
    })
}

export const deleteAdvertisement = (advertisementId: string) => async (dispatch: Dispatch, getState: GetState) => {
    const authSuperAdmin = getState().authSuperAdmin
    const config = getApiRequestConfig(authSuperAdmin?.['auth-token'])

    return api.delete(`advertisement/${advertisementId}`, config).then(response => {
        if (response.status === 200) {
            dispatch<DeleteAdvertisementAction>({
                type: AdvertisementActionTypes.DELETE_ADVERTISEMENT,
                data: advertisementId
            })
            return Promise.resolve<string>(response.data.message || 'Advertisement deleted')
        } else {
            throw { response }
        }
    }).catch((error) => {
        return Promise.reject<string>(apiErrorHandler(error, 'Unable to delete'))
    })
}
